// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import FinanceAccount from "./finance-account.js"
import FinanceDimensionValue from "./finance-dimension-value.js"

const modelClassData = {
  "attributes": {
    "finance_account_id": {
      "column": {
        "default": null,
        "name": "finance_account_id",
        "null": false,
        "type": "uuid"
      },
      "name": "finance_account_id",
      "selected_by_default": null,
      "translated": null
    },
    "finance_dimension_value_id": {
      "column": {
        "default": null,
        "name": "finance_dimension_value_id",
        "null": false,
        "type": "uuid"
      },
      "name": "finance_dimension_value_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "FinanceAccountFinanceDimensionValue",
  "collectionKey": "finance_account_finance_dimension_values",
  "collectionName": "finance_account_finance_dimension_values",
  "i18nKey": "finance_account_finance_dimension_value",
  "camelizedLower": "financeAccountFinanceDimensionValue",
  "name": "FinanceAccountFinanceDimensionValue",
  "nameDasherized": "finance-account-finance-dimension-value",
  "pluralName": "finance_account_finance_dimension_values",
  "ransackable_associations": [
    {
      "className": "FinanceAccount",
      "collectionName": "finance_accounts",
      "foreignKey": "finance_account_id",
      "name": "finance_account",
      "macro": "belongs_to",
      "resource_name": "FinanceAccount",
      "through": null
    },
    {
      "className": "FinanceDimensionValue",
      "collectionName": "finance_dimension_values",
      "foreignKey": "finance_dimension_value_id",
      "name": "finance_dimension_value",
      "macro": "belongs_to",
      "resource_name": "FinanceDimensionValue",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "finance_account_id",
      "column": {
        "default": null,
        "name": "finance_account_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "finance_dimension_value_id",
      "column": {
        "default": null,
        "name": "finance_dimension_value_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_finance_account_id",
      "column": {
        "default": null,
        "name": "old_finance_account_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_finance_dimension_value_id",
      "column": {
        "default": null,
        "name": "old_finance_dimension_value_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "FinanceAccount",
      "collectionName": "finance_accounts",
      "foreignKey": "finance_account_id",
      "name": "finance_account",
      "macro": "belongs_to",
      "resource_name": "FinanceAccount",
      "through": null
    },
    {
      "className": "FinanceDimensionValue",
      "collectionName": "finance_dimension_values",
      "foreignKey": "finance_dimension_value_id",
      "name": "finance_dimension_value",
      "macro": "belongs_to",
      "resource_name": "FinanceDimensionValue",
      "through": null
    }
  ],
  "paramKey": "finance_account_finance_dimension_value",
  "primaryKey": "id"
}

/** Frontend model for FinanceAccountFinanceDimensionValue. */
export default class FinanceAccountFinanceDimensionValue extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  financeAccountId() {
    return this.readAttributeUnderscore("finance_account_id")
  }

  /** @returns {boolean} */
  hasFinanceAccountId() {
    const value = this.financeAccountId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  financeDimensionValueId() {
    return this.readAttributeUnderscore("finance_dimension_value_id")
  }

  /** @returns {boolean} */
  hasFinanceDimensionValueId() {
    const value = this.financeDimensionValueId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {import("./finance-account.js").default | null} */
  financeAccount() {
    return this._readBelongsToReflection({modelClass: FinanceAccount, reflectionName: "finance_account"})
  }

  /** @returns {Promise<import("./finance-account.js").default | null>} */
  loadFinanceAccount() {
    if (!("financeAccountId" in this)) throw new Error("Foreign key method wasn't defined: financeAccountId")

    const id = this.financeAccountId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "finance_account",
        model: this,
        modelClass: FinanceAccount
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./finance-account.js").default | null>} */
  ensureFinanceAccountLoaded() {
    return this.ensureAssociationLoaded("finance_account")
  }

  /** @returns {import("./finance-dimension-value.js").default | null} */
  financeDimensionValue() {
    return this._readBelongsToReflection({modelClass: FinanceDimensionValue, reflectionName: "finance_dimension_value"})
  }

  /** @returns {Promise<import("./finance-dimension-value.js").default | null>} */
  loadFinanceDimensionValue() {
    if (!("financeDimensionValueId" in this)) throw new Error("Foreign key method wasn't defined: financeDimensionValueId")

    const id = this.financeDimensionValueId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "finance_dimension_value",
        model: this,
        modelClass: FinanceDimensionValue
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./finance-dimension-value.js").default | null>} */
  ensureFinanceDimensionValueLoaded() {
    return this.ensureAssociationLoaded("finance_dimension_value")
  }
}
